import { formatDate } from "@angular/common";
import { inject, Injectable } from "@angular/core";
import { ExportTypeEnum } from "@models/common/export-type.enum";
import { Tag } from "@models/common/tag";
import { ColumnWithExportName } from "@models/grid/column-with-export-name";
import { IndividualRecord } from "@models/individuals/Individual-record";
import {
  ExportEntityListModalComponent
} from "@shared/components/commons/export-company-list-modal/export-entity-list-modal.component";
import { DinPipe } from "@shared/pipes/din.pipe";
import { excelReportTitleStyles, excelTitleForReport } from "@shared/utils/excel-title-for-report";
import {
  ColDef,
  CsvExportParams,
  ExcelExportParams,
  ExcelRow,
  ExcelStyle,
  GridApi,
  ProcessCellForExportParams,
  ProcessHeaderForExportParams
} from "ag-grid-community";
import { deepClone } from "fast-json-patch";
import { take } from "rxjs";
import { ModalFormsService } from "../../../forms/services/modal-forms.service";

@Injectable()
export class IndividualsExportService {
  modalFormsService = inject(ModalFormsService);

  readonly excelStyles: ExcelStyle[] = excelReportTitleStyles;

  exportIndividualList(
    gridApi: GridApi,
    colDefs: ColDef[],
    selectedIndividuals: IndividualRecord[],
    totalRows: number,
    isBulkExport: boolean,
    exportType: ExportTypeEnum,
  ): void {
    const modalRef = this.modalFormsService.openModal(ExportEntityListModalComponent, { modalDialogClass: 'export-company-list' });
    const componentInstance = modalRef.componentInstance as ExportEntityListModalComponent;

    componentInstance.colDefs = deepClone(colDefs) as ColumnWithExportName[];
    componentInstance.title = 'Export Individual List';
    componentInstance.subTitle = 'individual selected';
    componentInstance.numberOfCompanies = isBulkExport ? selectedIndividuals.length : totalRows;
    componentInstance.exportType = exportType;

    componentInstance.confirm
      .pipe(take(1))
      .subscribe((columnForExport: string[]) => {
        const numberColumnForExport = columnForExport.length - 1;
        const fileName = 'Individuals ' + formatDate(new Date(), 'dd-MM-yyyy', 'en-US');

        if (exportType === ExportTypeEnum.EXCEL) {
          const params: ExcelExportParams = isBulkExport
            ? { columnKeys: columnForExport, onlySelected: true }
            : { columnKeys: columnForExport };
          const exportParamsXls = this.exportParamsXls(numberColumnForExport) as ExcelExportParams;
          gridApi.exportDataAsExcel({ ...exportParamsXls, ...params, fileName });
        } else if (exportType === ExportTypeEnum.CSV) {
          const params: CsvExportParams = isBulkExport
            ? { columnKeys: columnForExport, onlySelected: true }
            : { columnKeys: columnForExport };
          gridApi.exportDataAsCsv({ ...params, fileName });
        }
      });
  }

  exportParamsXls(numberColumnForExport: number = 0): ExcelExportParams {
    return {
      ...this.exportParams(),
      prependContent: this.getExcelTitleRows(numberColumnForExport)
    } as ExcelExportParams;
  }

  exportParamsCsv(): CsvExportParams {
    return this.exportParams() as CsvExportParams;
  }

  exportParams() {
    return {
      processCellCallback(params: ProcessCellForExportParams): string {
        const value: unknown = params.value;
        const individualData = params.node?.data as IndividualRecord;
        if (params.column.getColId() === 'tags') {
          return (value as Tag[] ?? []).map((tag: Tag) => tag.name).join(', ');
        } else if (params.column.getColId() === 'dob') {
          return individualData.dob ? formatDate(individualData.dob ?? '', 'dd MMM yyyy', 'en') : '';
        } else if (params.column.getColId() === 'din') {
          return individualData.din ? DinPipe.formatValue(individualData.din) : '';
        } else if (params.column.getColId() === 'fullNameStored') {
          return individualData.getFullName();
        }

        return value as string;
      },
      processHeaderCallback(params: ProcessHeaderForExportParams) {
        const colDef = params.column.getColDef() as ColumnWithExportName;
        return colDef?.exportColumnName ?? colDef.headerName;
      },
      sheetName: 'Individual'
    };
  }

  getExcelTitleRows: (numberColumnForExport: number) => ExcelRow[] = (numberColumnForExport) => {
    return excelTitleForReport(numberColumnForExport, 'Individuals Report');
  };

}
